/**
 *  Action Constants
 */
import { Dispatch, ExtraArgs, GetState } from '../../types'
import * as selectors from './selectors'
import {
  getComponentsResourceType, getFileUrl,
  getRawDataResourceTypeFromResource,
  getStringCurrentDate,
  LogFile,
  rawDataOrigin,
  RawDataResourceType
} from './model'
import { ExternalProps } from './RawDataPage'
import { getEnvironmentSelector, getResourceSelector, getSelectedDate } from './selectors'
import { getResourceById, getResourceId, isAsset, Resource } from '../../common/resource/model';
import {
  fetchEnvironments,
  selectEnvironmentForEnvironments,
  storeEnvironmentsAndSelectOne
} from '../../common/environment/actions';
import {
  fetchAllResources,
  fetchLoadingResources,
  selectAnypointResource,
  storeResourcesAndSelectOne
} from '../../common/resource/actions';
import { SelectorOptions } from '../../common/selector/model';
import {
  selectResourceTargets,
  fetchAllRawDataTargets,
  setTargets
} from '../../common/resource/target/actions';
import { BasicTarget } from '../../common/resource/target/model';
import MonitoringLocalStorageManager from '../../util/storage/MonitoringLocalStorageManager';
import { goToRawData } from '../Sidebar/Urls';
import { EnvironmentSelectorState } from '../../common/environment/reducer';
import { ResourceSelectorState } from '../../common/resource/reducer';
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource';
import { Environment } from '../../remote-api/models';
import { ResourceNotFoundError } from '../../common/error/ResourceNotFoundError';
import { fetchVersionsAndSelect } from '../../common/selector/actions';

export const Loading = 'Monitoring/RawData/Loading';
export const ChangeDate = 'Monitoring/RawData/ChangeDate';
export const AllTargets = 'Monitoring/RawData/AllTargets';
export const ChangeLogFiles = 'Monitoring/RawData/ChangeLogFiles';
export const DownloadLogFile = 'Monitoring/RawData/DownloadLogFile';
export const AvailableFiles = 'Monitoring/RawData/AvailableFiles';
export const SetStateFromLanding = 'Monitoring/RawData/SetStateFromLanding'

/**
 *  Action Type
 */
export type LoadingAction = {
  type: 'Monitoring/RawData/Loading'
}

export type ChangeDateAction = {
  type: 'Monitoring/RawData/ChangeDate'
  payload: {
    selectedDate: string
  }
}

export type ChangeLogFilesAction = {
  type: 'Monitoring/RawData/ChangeLogFiles'
  payload: {
    logFiles: LogFile[]
  }
}

export type DownloadLogFileAction = {
  type: 'Monitoring/RawData/DownloadLogFile'
  payload: {
    logFileKey: string
  }
}

export type InitializeRawDataAction = {
  type: 'Monitoring/RawData/InitializeRawData'
}

export type AvailableFilesAction = {
  type: 'Monitoring/RawData/AvailableFiles'
  payload: {
    files: number
  }
}

export type AllTargetsAction = {
  type: 'Monitoring/RawData/AllTargets'
  payload: {
    targets: any
  }
}

/**
 *  Action Creators
 */
export const initializeLoadingAction = (): LoadingAction => ({
  type: Loading,
});

export type SetStateFromLandingAction = {
  type: 'Monitoring/RawData/SetStateFromLanding',
  payload: {
    environmentSelector: EnvironmentSelectorState,
    resourceSelector: ResourceSelectorState
  }
}

export const changeSelectedDateAction = (selectedDate: string): ChangeDateAction => ({
  type: ChangeDate,
  payload: {selectedDate}
});

export const availableFilesAction = (files: number): AvailableFilesAction => ({
  type: AvailableFiles,
  payload: {files}
});

export const changeLogFiles = (logFiles: LogFile[]): ChangeLogFilesAction => ({
  type: ChangeLogFiles,
  payload: {logFiles}
});

export const downloadedLogFileAction = (logFileKey: string): DownloadLogFileAction => ({
  type: DownloadLogFile,
  payload: {logFileKey}
});

export const allTargets = (targets: any): AllTargetsAction => ({
  type: AllTargets,
  payload: {targets}
});

export const initializeLoading = () => (dispatch: Dispatch) => {
  dispatch(initializeLoadingAction())
}

export const setStateFromLandingAction = (environmentSelector: EnvironmentSelectorState,
                                          resourceSelector: ResourceSelectorState): RawDataActions => ({
  type: SetStateFromLanding,
  payload: {
    environmentSelector: environmentSelector,
    resourceSelector: resourceSelector
  }
})

export const baseRawDataOptions = {origin: rawDataOrigin, lsOrigin: rawDataOrigin}

export const rawDataSelectorOptions = (options: SelectorOptions) => ({...options, block: {apis: true, targets: true}})

export const landingOnLoad = () => (dispatch: Dispatch, getState: GetState) => {
  const rawDataState = getState().monitoring.rawData

  const environmentSelector = rawDataState.environmentSelector
  const resourceSelector = rawDataState.resourceSelector

  const environment = environmentSelector.selected
  const resource = resourceSelector.selected

  if (environment && resource) {
    const resourceType = getRawDataResourceTypeFromResource(resource) || RawDataResourceType.cloudhub
    goToRawData(environment.id, resourceType, getResourceId(resource), getStringCurrentDate())
  } else {
    dispatch(fetchSelectorsAndSelect(rawDataSelectorOptions(baseRawDataOptions)))
  }
}

export const onLoad = (props: ExternalProps) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const {environmentSelector, selectedDate, targetsSelector} = getState().monitoring.rawData
  const resourceSelector = getResourceSelector.getAll(getState())
  const componentsResourceType = getComponentsResourceType(props.resourceType)
  const date = props.date || getStringCurrentDate()
  const currentTargets = targetsSelector.selectedResources
  if (selectedDate !== date) {
    dispatch(changeSelectedDate(date))
  }

  dispatch(initializeLoading())

  const environment = environmentSelector.resources && environmentSelector.resources.find(env => env.id === props.environmentId)
  const resource = resourceSelector.resources &&
      getResourceById(resourceSelector.resources, props.resourceId, componentsResourceType)

  const options = rawDataSelectorOptions({...baseRawDataOptions, resourceType: componentsResourceType})

  // check if selected environment is in store
  if (environment && environmentSelector.resources) {
    dispatch(storeEnvironmentsAndSelectOne(environment, environmentSelector.resources, rawDataOrigin))
    // check if selected resource is in store
    if (resource && resourceSelector.resources) {
      dispatch(fetchLoadingResources(options, props.environmentId, resourceSelector))
      dispatch(storeResourcesAndSelectOne(resource, resourceSelector.resources, rawDataOrigin))

      dispatch(fetchResourceTargetsAndSelect(resource, props.environmentId))
          .then(() => {
            const targets = selectors.getAllTargets(getState())
            dispatch(fetchLogFiles(environment.id, resource, date, props.resourceType, targets, options.origin))
          })
    } else { // the selected resource is not in the store, fetch all resources
      dispatch(fetchAllResourcesAndTargets({...options, shouldSelect: true}, environment, props.resourceId))
          .then(() => {
            const selectedResource = getResourceSelector.getAll(getState()).selected
            const targets = selectors.getAllTargets(getState())
            if (selectedResource) dispatch(fetchLogFiles(environment.id, selectedResource, date, props.resourceType, targets, options.origin ))
          })
    }
  } else { // the selected environment is not in the store, fetch all environments
    dispatch(fetchSelectorsAndSelect(options, props.environmentId, props.resourceId))
        .then(() => {
          const selectedEnvironment = getEnvironmentSelector.selected(getState())
          const selectedResource = getResourceSelector.getAll(getState()).selected
          const targets = selectors.getAllTargets(getState())
          if (selectedEnvironment && selectedResource) {
            dispatch(fetchLogFiles(selectedEnvironment.id, selectedResource, date, props.resourceType, targets, options.origin))
          }
        })
  }
}

export const onLandingView = (environmentId: string, resourceId: string) =>
    (dispatch: Dispatch, getState: GetState) => {
  const environmentSelector = getEnvironmentSelector.getAll(getState())
  const resourceSelector = getResourceSelector.getAll(getState())

  const resource = resourceSelector.selected
  if (resource && getResourceId(resource) === resourceId) {
    dispatch(setStateFromLandingAction(environmentSelector, resourceSelector))

    goToRawData(environmentId, getRawDataResourceTypeFromResource(resource) || RawDataResourceType.cloudhub, resourceId, getStringCurrentDate())
  }
}

export const onView = (props: ExternalProps) => {
  const rawDataLS = MonitoringLocalStorageManager.get(rawDataOrigin)

  if (rawDataLS) {
    const value = {
      ...rawDataLS,
      selectedEnvironment: props.environmentId,
      selectedResource: props.resourceId,
      selectedResourceType: props.resourceType
    }

    MonitoringLocalStorageManager.put(rawDataOrigin, value)
  }

  goToRawData(props.environmentId, props.resourceType, props.resourceId, props.date)
}

export const fetchLogFiles = (envId: string,
                              resource: Resource,
                              date: string,
                              resourceType: RawDataResourceType,
                              targets?: BasicTarget[],
                              origin?: string, firstLoad = true) =>
    (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {

  const orgId = getState().monitoring.auth.profile.organizationId
  const transformedDate = transformDate(date)
  // const fileIds = getResourceLogsId(resource, targets)
  const fileIds = targets === undefined ? [] : targets
  const archiveAPI = api.archiveAPI(getState)

  const getLogFile = (resourceType: RawDataResourceType) => {
    if (resourceType === RawDataResourceType.hybrid) {
      return archiveAPI.fetchHybridLogFiles
    } else {
      return archiveAPI.fetchLogFiles
    }
  }

  const eventualFiles: Promise<LogFile[]>[] = fileIds.map(resourceLogsId => getLogFile(resourceType)(orgId, envId, resourceLogsId.id, transformedDate)
      .then(response => {
        return response.resources.map(file => {
          const fileIdArray = file.id.split('.')
          fileIdArray.shift()

          const fileName = `${resourceLogsId.id}.${fileIdArray.join('.')}`

          return {
            envId: envId,
            appId: getResourceId(resource),
            fileName: fileName,
            fileUrl: getFileUrl(orgId, envId, resourceLogsId.id, transformedDate, file.id, resource),
            fileKey: file.id,
            workerId: resourceLogsId.name,
            size: file.size,
            date: transformedDate,
            appFolderName: resourceLogsId.id
          }
        })
      })
  )

  return Promise.all(eventualFiles).then((filesArray: LogFile[][]) => {
    const files = filesArray.reduce((a, b) => a.concat(b), [])

    if (targets && firstLoad){
      // from the list of files fetched from S3, finding unique targets which in turn would be used to load the selector
      dispatch<Promise<BasicTarget[]>>(setTargets([...new Set(files.map(r => r.workerId))]
        .map(r => { const target = fileIds[fileIds.findIndex(x => x.name === r)]
                    return {id: target.id, name: target.name }}), origin))
                    .then(targetsResult => {dispatch(selectResourceTargets(targetsResult, origin))})}
    dispatch(availableFilesAction(files.length))
    dispatch(changeLogFiles(files))

    return files
  })
}

export const changeSelectedDate = (selectedDate: string) => (dispatch: Dispatch) => {
  dispatch(changeSelectedDateAction(selectedDate));
};

export const onTargetsChange = (targets: BasicTarget[]) => (dispatch: Dispatch, getState: GetState) => {
  const environment = getEnvironmentSelector.selected(getState())
  const resource = getResourceSelector.selected(getState())
  const date = getSelectedDate(getState())

  dispatch(selectResourceTargets(targets, rawDataOrigin))

  if (environment && resource) {
    dispatch(initializeLoading())
    dispatch(fetchLogFiles(environment.id, resource, date, getRawDataResourceTypeFromResource(resource) || RawDataResourceType.cloudhub,
                           targets, rawDataOrigin, false))
  }
}

export const downloadLogFile = (fileName: string) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  dispatch(downloadedLogFileAction(fileName));
};

export const environmentSelected = (environmentId: string) => (dispatch: Dispatch, getState: GetState) => {
  const environmentSelector = getEnvironmentSelector.getAll(getState())

  dispatch(selectEnvironmentAndFetchAllResources(rawDataSelectorOptions(baseRawDataOptions), environmentId, environmentSelector.resources))
}

export const resourceSelected = (resourceId: string, resourceType: ResourceType) => (dispatch: Dispatch, getState: GetState) => {
  const resourceSelector = getResourceSelector.getAll(getState())
  const opts = {...baseRawDataOptions, resourceType}

  dispatch(selectResourceAndFetchTargets(rawDataSelectorOptions(opts), resourceId, resourceSelector.resources))
}

export const fetchAllResourcesAndTargets = (options: SelectorOptions,
                                            environment: Environment,
                                            resourceId?: string,
                                            targets?: BasicTarget[]) => (dispatch: Dispatch) => {
  if (options.shouldSelect) {
    return dispatch<Promise<Resource[]>>(fetchAllResources(options, environment.id)).then(resources => {
      const maybeResource: Resource | undefined = dispatch(selectAnypointResource(options, resources, resourceId))
      if (maybeResource) {
        return dispatch(fetchResourceTargetsAndSelect(maybeResource, environment.id))
      } else {
        dispatch(selectResourceTargets( undefined, options.origin))

        return Promise.resolve()
      }
    })
  } else {
    dispatch(selectAnypointResource(options, [], undefined))
    dispatch(selectResourceTargets(undefined, options.origin))

    return dispatch<Promise<Resource[]>>(fetchAllResources(options, environment.id)).then(resources => {
      return Promise.resolve()
    })
  }
}

const fetchSelectorsAndSelect = (options: SelectorOptions, envId?: string, resourceId?: string) =>
    (dispatch: Dispatch): Promise<void> => {
      return dispatch<Promise<Environment[]>>(fetchEnvironments(options.origin)).then(environments => {
        const maybeEnvironment: Environment | undefined = dispatch(selectEnvironmentForEnvironments(options, environments, envId))
        if (!maybeEnvironment) {
          throw new ResourceNotFoundError(`Environment not found. envId:${envId}`)
        }
        return dispatch(fetchAllResourcesAndTargets({...options, shouldSelect: true}, maybeEnvironment, resourceId))
      })
    }

export const selectEnvironmentAndFetchAllResources = (options: SelectorOptions, envId: string, environments?: Environment[]) =>
    (dispatch: Dispatch) => {
      if (environments) {
        const maybeEnvironment = dispatch<Environment | undefined>(selectEnvironmentForEnvironments(options, environments, envId))
        if (maybeEnvironment) {
          return dispatch(fetchAllResourcesAndTargets(options, maybeEnvironment))
        }
        return Promise.resolve()
      }
      return Promise.resolve()
    }

export const selectResourceAndFetchTargets = (options: SelectorOptions, resourceId: string, resources?: Resource[]) =>
    (dispatch: Dispatch) => {
      if (resources) {
        const maybeResource = dispatch<Resource | undefined>(selectAnypointResource(options, resources, resourceId))
        if (maybeResource && isAsset(maybeResource)) {
          dispatch(fetchVersionsAndSelect(options, maybeResource, undefined))
        }
      }
    }

const fetchResourceTargetsAndSelect = (resource: Resource, env: string) =>
    (dispatch: Dispatch) => {

      return dispatch<Promise<BasicTarget[]>>(fetchAllRawDataTargets(resource, env)).then(targets => {
        dispatch(allTargets(targets))
       // let targetsResult = targets
        // const maybeTargets = selectedTargets && selectedTargets.filter(st => targets.findIndex(t => t.id === st.id) !== -1)

       // if (selectedTargets && maybeTargets && maybeTargets.length === selectedTargets.length) targetsResult = selectedTargets
      //  dispatch(allTargets(targetsResult))
      })
    }

function transformDate(date: string) {
  const resolvedDate = date || getStringCurrentDate()

  return {
    month: resolvedDate.split('/')[0],
    day: resolvedDate.split('/')[1],
    year: resolvedDate.split('/')[2],
    hours: '00',
    minutes: '00'
  }
}

export type RawDataActions =
  | SetStateFromLandingAction
  | AllTargetsAction
  | ChangeLogFilesAction
  | ChangeDateAction
  | DownloadLogFileAction
  | InitializeRawDataAction
  | AvailableFilesAction
  | LoadingAction
